import { useQuery } from '@apollo/client';
import { graphql } from '../gql';
import { useMemo } from 'react';
import { GetIndustrySectorsQuery } from '../gql/graphql';
import { getPaginatedNodes } from './utils';

const GET_INDUSTRY_SECTORS = graphql(`
  query getIndustrySectors(
    $pagination: PaginationRequest
    $filters: IndustrySectorFilterInput
  ) {
    industrySectors(pagination: $pagination, filters: $filters) {
      pageInfo {
        totalResults
        hasNextPage
        pageNumber
      }
      edges {
        cursor
        node {
          name
          uuid
          naicsCode
        }
      }
    }
  }
`);

export function useGetIndustrySectorsQuery(first = 2000) {
  const { loading, error, data, fetchMore } = useQuery(GET_INDUSTRY_SECTORS, {
    variables: {
      pagination: { after: 0, first },
      filters: {},
    },
    onCompleted: (data) => {
      if (data.industrySectors.pageInfo.hasNextPage) {
        fetchMore({
          variables: {
            pagination: {
              after: data.industrySectors.pageInfo.pageNumber + 1,
              first,
            },
            filters: {},
          },
          updateQuery: (previousData, { fetchMoreResult: newData }) => {
            const mergedData: GetIndustrySectorsQuery = {
              ...previousData,
              ...newData,
              industrySectors: {
                ...previousData.industrySectors,
                ...newData.industrySectors,
                pageInfo: {
                  ...previousData.industrySectors.pageInfo,
                  ...newData.industrySectors.pageInfo,
                },
                edges: [
                  ...previousData.industrySectors.edges,
                  ...newData.industrySectors.edges,
                ],
              },
            };
            return mergedData;
          },
        });
      }
    },
  });

  const industrySectors = useMemo(() => {
    return getPaginatedNodes(data?.industrySectors);
  }, [data]);

  return {
    loading,
    error,
    data,
    industrySectors,
  };
}

export function useGetIndustrySectorsPagedQuery({
  pageNumber = 0,
  pageSize = 50,
  enabled = true,
  name,
}: {
  pageNumber?: number;
  pageSize?: number;
  enabled?: boolean;
  name?: string;
}) {
  const { loading, error, data, fetchMore } = useQuery(GET_INDUSTRY_SECTORS, {
    variables: {
      pagination: { after: pageNumber * pageSize, first: pageSize },
      filters: { name: name || null },
    },
    skip: !enabled,
    fetchPolicy: 'network-only',
  });

  const hasNextPage = data?.industrySectors?.pageInfo?.hasNextPage ?? false;

  return {
    loading,
    error,
    data,
    fetchMore,
    hasNextPage,
  };
}
